import React, { useState } from 'react';
import { FaImage } from 'react-icons/fa';

// Definición de tamaños estándar para diferentes tipos de imágenes
export const IMAGE_SIZES = {
  product: { width: 400, height: 400, aspectRatio: '1/1' },
  category: { width: 600, height: 400, aspectRatio: '3/2' },
  detail: { width: 800, height: 800, aspectRatio: '1/1' },
  thumbnail: { width: 80, height: 80, aspectRatio: '1/1' }
};

/**
 * Procesa una URL de imagen para manejar diferentes formatos
 * @param {any} imageData - Datos de la imagen (URL, objeto con propiedad url, etc.)
 * @param {string} fallbackText - Texto para mostrar en el placeholder
 * @returns {string} - URL válida de imagen o URL de placeholder
 */
export const processImageUrl = (imageData, fallbackText = 'Producto') => {
  // Si no hay datos de imagen, devolver placeholder
  if (!imageData) {
    return `https://placehold.co/400x400/e9ecef/495057?text=${encodeURIComponent(fallbackText)}`;
  }
  
  try {
    // Caso 1: URL como string
    if (typeof imageData === 'string') {
      // URL absoluta
      if (imageData.startsWith('http')) {
        return imageData;
      }
      
      // URL de Parse Server
      if (imageData.startsWith('files/')) {
        return `https://parsefiles.back4app.com/${imageData}`;
      }
      
      // Ruta relativa
      return imageData.startsWith('/') ? imageData : `/${imageData}`;
    }
    
    // Caso 2: Objeto con propiedad url (formato común de respuesta API)
    if (imageData && typeof imageData === 'object' && imageData.url) {
      return imageData.url;
    }
    
    // Caso 3: Objeto Parse File
    if (imageData && typeof imageData === 'object' && imageData._url) {
      return imageData._url;
    }
    
    // Por defecto: devolver placeholder
    return `https://placehold.co/400x400/e9ecef/495057?text=${encodeURIComponent(fallbackText)}`;
  } catch (error) {
    console.error('Error procesando URL de imagen:', error);
    return `https://placehold.co/400x400/e9ecef/495057?text=${encodeURIComponent(fallbackText)}`;
  }
};

/**
 * Componente para mostrar imágenes con tamaños estandarizados
 */
const StandardImage = ({ 
  image, 
  type = 'product', 
  alt = 'Imagen', 
  className = '', 
  containerClassName = '',
  onError = null
}) => {
  const [imageError, setImageError] = useState(false);
  const size = IMAGE_SIZES[type] || IMAGE_SIZES.product;
  const imageUrl = processImageUrl(image, alt);
  
  const handleImageError = (e) => {
    setImageError(true);
    if (onError) {
      onError(e);
    } else {
      e.target.onerror = null;
      e.target.src = `https://placehold.co/${size.width}x${size.height}/e9ecef/495057?text=${encodeURIComponent(alt)}`;
    }
  };

  return (
    <div 
      className={`standard-image-container ${containerClassName}`} 
      style={{ 
        aspectRatio: size.aspectRatio,
        maxWidth: `${size.width}px`,
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {!imageError ? (
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          className={`w-100 h-100 object-fit-cover ${className}`}
          onError={handleImageError}
          style={{ objectPosition: 'center' }}
        />
      ) : (
        <div 
          className="d-flex align-items-center justify-content-center bg-light w-100 h-100"
        >
          <div className="text-center p-2">
            <FaImage className="text-secondary" style={{ fontSize: '2rem' }} />
            <p className="text-muted small mt-2">{alt}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardImage;
